import { useMemo } from "react";

import { Box, Tabs } from "@mantine/core";
import { format } from "@react-input/mask";
import { useTranslation } from "react-i18next";

import { useFormAutosave } from "hooks";
import { AppConstants } from "constant";
import { CreditService } from "services";
import { displayClasses } from "styles";
import { CreditOptionsResponse, CreditRequestForm, CreditResponse } from "types";
import { AppUtils } from "utils";

import { CreditComparisonSource } from "./comparison-source";
import CreditRequestIncomes from "./incomes";
import CreditRequestOutcomes from "./outcomes";
import CreditRequestPersonal from "./personal";
import CreditRequestRequest from "./request";

interface CreditRequestProps {
  credit: CreditResponse;
  options: CreditOptionsResponse;
  readOnly?: boolean;
  updateService?: (id: number, data: any) => Promise<void>;
  comparisonSource?: CreditComparisonSource | null;
}

const TABS = {
  CREDIT: "credit",
  PERSONAL: "personal",
  INCOMES: "incomes",
  OUTCOMES: "outcomes",
};

const CreditRequest = ({
  credit,
  options,
  readOnly = false,
  updateService = CreditService.creditsUpdateMultipleProperties,
  comparisonSource,
}: CreditRequestProps) => {
  const { t } = useTranslation();
  const effectiveComparisonSource = readOnly ? null : comparisonSource;

  const { form, visibleFields } = useFormAutosave<CreditRequestForm>({
    credit,
    updateService,
    getVisibleFieldsFn: AppUtils.getVisibleCreditRequestFields,
    readOnly,
    initialValuesTransform: (credit: CreditResponse) => ({
      ...credit,
      partnerPhoneNumber: credit.partnerPhoneNumber ? format(credit.partnerPhoneNumber, { mask: AppConstants.MASK_SWISS_PHONE_NUMBER, replacement: { _: /\d/ } }) : credit.partnerPhoneNumber,
      user: {
        ...credit.user,
        firstName: credit.user.fullName.split(" ")[0],
        lastName: credit.user.fullName.split(" ")[1],
        phone: credit.user.countryCode + " " + credit.user.phoneNumber,
      },
      birthDate: credit.birthDate ? new Date(credit.birthDate) : undefined,
      visaFrom: credit.visaFrom ? new Date(credit.visaFrom) : undefined,
      livingFrom: credit.livingFrom ? new Date(credit.livingFrom) : undefined,
      partnerBirthDate: credit.partnerBirthDate ? new Date(credit.partnerBirthDate) : undefined,
      partnerVisaFrom: credit.partnerVisaFrom ? new Date(credit.partnerVisaFrom) : undefined,
      saturdayAvailability: Boolean(
        credit.saturdayAvailabilityFrom || credit.saturdayAvailabilityTo,
      ),
      partnerSaturdayAvailability: Boolean(
        credit.partnerSaturdayAvailabilityFrom || credit.partnerSaturdayAvailabilityTo,
      ),
      additionalExpenses: Boolean(
        credit.expenses?.[0]?.expenseType || credit.expenses?.[0]?.amount,
      ),
    }),
  });

  const tabs = useMemo(
    () => [
      {
        value: TABS.CREDIT,
        component: (
          <CreditRequestRequest
            form={form}
            options={options}
            visibleFields={visibleFields}
            readOnly={readOnly}
            comparisonSource={effectiveComparisonSource}
          />
        ),
      },
      {
        value: TABS.PERSONAL,
        component: (
          <CreditRequestPersonal
            credit={credit}
            form={form}
            options={options}
            visibleFields={visibleFields}
            readOnly={readOnly}
            comparisonSource={effectiveComparisonSource}
          />
        ),
      },
      {
        value: TABS.INCOMES,
        component: (
          <CreditRequestIncomes
            credit={credit}
            form={form}
            options={options}
            readOnly={readOnly}
            comparisonSource={effectiveComparisonSource}
          />
        ),
      },
      {
        value: TABS.OUTCOMES,
        component: (
          <CreditRequestOutcomes
            credit={credit}
            form={form}
            visibleFields={visibleFields}
            options={options}
            readOnly={readOnly}
            comparisonSource={effectiveComparisonSource}
          />
        ),
      },
    ],
    [form, options, credit, visibleFields, readOnly, effectiveComparisonSource],
  );

  return (
    <Box>
      <Tabs defaultValue={tabs[0].value} variant={"pills"} mt={"lg"}>
        <Tabs.List grow={true}>
          {tabs.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value} className={displayClasses.tabView}>
              {t("credit.view.tab." + tab.value)}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {tabs.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value} className={displayClasses.tabContent}>
            {tab.component}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Box>
  );
};

export default CreditRequest;
